import {Center, Loader} from "@mantine/core";
import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
} from "@tanstack/react-router";
import {AuthGuard} from "@/app/auth/components/AuthGuard";
import {ShellLayout} from "@/app/ui/Shell/ShellLayout";
import type {StudyMode} from "@/shared/types";
import {STUDY_MODES} from "@/shared/types";

// Loading Component
const PageLoader = () => (
  <Center h="100vh">
    <Loader color="teal" size="xl" />
  </Center>
);

// Root Layout
const rootRoute = createRootRoute({
  component: () => <Outlet />,
  pendingComponent: PageLoader,
});

// Public Routes
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: lazyRouteComponent(() =>
    import("@/app/pages/LandingPage").then(m => ({default: m.LandingPage})),
  ),
});

const callbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/callback",
  component: lazyRouteComponent(() =>
    import("@/app/pages/CallbackPage").then(m => ({default: m.CallbackPage})),
  ),
});

// Authenticated Layout (Guards access)
const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "auth",
  component: AuthGuard,
});

// 1. App Shell Layout (Sidebar + Navbar)
const appShellRoute = createRoute({
  getParentRoute: () => authRoute,
  id: "app-shell",
  component: ShellLayout,
});

const dashboardRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: "dashboard",
  component: lazyRouteComponent(() =>
    import("@/app/pages/DashboardPage").then(m => ({default: m.DashboardPage})),
  ),
});

const settingsRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: "settings",
  component: lazyRouteComponent(() =>
    import("@/app/pages/SettingsPage").then(m => ({default: m.SettingsPage})),
  ),
});

// Profile & Social (Inside App Shell)
const profileRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: "profile",
  component: () => <Outlet />,
});

const profileIndexRoute = createRoute({
  getParentRoute: () => profileRoute,
  path: "/",
  component: lazyRouteComponent(() =>
    import("@/app/pages/ProfilePage").then(m => ({default: m.ProfilePage})),
  ),
});

const profileDidRoute = createRoute({
  getParentRoute: () => profileRoute,
  path: "$did",
  component: lazyRouteComponent(() =>
    import("@/app/pages/ProfilePage").then(m => ({default: m.ProfilePage})),
  ),
});

// Library Management (Inside App Shell)
const libraryRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: "library",
  component: () => <Outlet />,
});

const libraryIndexRoute = createRoute({
  getParentRoute: () => libraryRoute,
  path: "/",
  component: lazyRouteComponent(() =>
    import("@/app/pages/LibraryPage").then(m => ({default: m.LibraryPage})),
  ),
});

const libraryNewRoute = createRoute({
  getParentRoute: () => libraryRoute,
  path: "new",
  component: lazyRouteComponent(() =>
    import("@/app/pages/LibraryNewPage").then(m => ({default: m.LibraryNewPage})),
  ),
});

const libraryNewSingleRoute = createRoute({
  getParentRoute: () => libraryRoute,
  path: "new-single",
  component: lazyRouteComponent(() =>
    import("@/app/pages/LibraryNewSinglePage").then(m => ({
      default: m.LibraryNewSinglePage,
    })),
  ),
});

const libraryEntryDetailRoute = createRoute({
  getParentRoute: () => libraryRoute,
  path: "entry/$rkey",
  component: lazyRouteComponent(() =>
    import("@/app/pages/LibraryEntryEditPage").then(m => ({
      default: m.LibraryEntryEditPage,
    })),
  ),
});

// 2. Study Layout (Zen Mode / No Sidebar) - Sibling to App Shell
const studyRoute = createRoute({
  getParentRoute: () => authRoute, // Direct child of auth, bypassing ShellLayout
  path: "study",
  component: () => <Outlet />,
});

const studyIndexRoute = createRoute({
  getParentRoute: () => studyRoute,
  path: "/",
  component: () => <div>Study Dashboard</div>,
});

const studyReviewRoute = createRoute({
  getParentRoute: () => studyRoute,
  path: "review",
  validateSearch: (search: Record<string, unknown>): {mode?: StudyMode} => {
    const mode = search.mode as string;
    return {
      mode: STUDY_MODES.includes(mode as StudyMode) ? (mode as StudyMode) : undefined,
    };
  },
  component: lazyRouteComponent(() =>
    import("@/app/pages/StudyReviewPage").then(m => ({default: m.StudyReviewPage})),
  ),
});

const studyCramRoute = createRoute({
  getParentRoute: () => studyRoute,
  path: "cram",
  component: () => <Outlet />,
});

const studyCramIndexRoute = createRoute({
  getParentRoute: () => studyCramRoute,
  path: "/",
  component: () => <div>Cram Mode Selection</div>,
});

const studyCramGroupingRoute = createRoute({
  getParentRoute: () => studyCramRoute,
  path: "$grouping",
  validateSearch: (search: Record<string, unknown>): {mode?: StudyMode} => {
    const mode = search.mode as string;
    return {
      mode: STUDY_MODES.includes(mode as StudyMode) ? (mode as StudyMode) : undefined,
    };
  },
  component: lazyRouteComponent(() =>
    import("@/app/pages/StudyReviewPage").then(m => ({default: m.StudyReviewPage})),
  ),
});

const studySessionRoute = createRoute({
  getParentRoute: () => studyRoute,
  path: "session/$rkey",
  component: () => <div>Session Summary</div>,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  callbackRoute,
  authRoute.addChildren([
    appShellRoute.addChildren([
      dashboardRoute,
      settingsRoute,
      profileRoute.addChildren([profileIndexRoute, profileDidRoute]),
      libraryRoute.addChildren([
        libraryIndexRoute,
        libraryNewRoute,
        libraryNewSingleRoute,
        libraryEntryDetailRoute,
      ]),
    ]),
    studyRoute.addChildren([
      studyIndexRoute,
      studyReviewRoute,
      studyCramRoute.addChildren([studyCramIndexRoute, studyCramGroupingRoute]),
      studySessionRoute,
    ]),
  ]),
]);

export const router = createRouter({routeTree});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
